import React, { useEffect, useMemo } from 'react';
import {
	SubscriptionMembership,
	useWorkplaceActiveSubscriptionMembershipQuery,
	useWorkplaceSlugQuery,
} from '../../../graphql/graphql';
import ORSMOnboarding from './Onboarding/ORSMOnboarding';
import Loading from '../../../Components/Loading';
import ORSMDashboard from './Dashboard/ORSMDashboard';
import { useDisclose } from '../../Modal/Hooks/useDisclose';
import ORSMActivationConfirmationModal from './ActivationConfirmation/ORSMActivationConfirmationModal';
import ORSMActivationFailedModal from './ActivationFailed/ORSMActivationFailedModal';

interface Props {
	rootWorkplaceId: string;
}

const ORSMWrapper: React.FC<Props> = ({ rootWorkplaceId }) => {
	const {
		isOpen: isOpenSuccess,
		onOpen: onOpenSuccess,
		onClose: onCloseSuccess,
	} = useDisclose(false);
	const {
		isOpen: isOpenFailed,
		onOpen: onOpenFailed,
		onClose: onClosFailed,
	} = useDisclose(false);
	const { data, isLoading, isFetching, refetch } = useWorkplaceSlugQuery({
		id: rootWorkplaceId,
	});

	const {
		data: activeMembershipData,
		isLoading: isLoadingActiveMembershipData,
		isFetching: isFetchingActiveMembershipData,
		refetch: refetchActiveMembershipData,
	} = useWorkplaceActiveSubscriptionMembershipQuery({
		where: {
			code: 'osmr_workplace_subscription',
			workplaceId: rootWorkplaceId,
		},
	});

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const activation = params.get('activation');
		if (activation === 'success') {
			onOpenSuccess();
		}
		if (activation === 'failed') {
			onOpenFailed();
		}
	}, []);

	const removeActivationParameter = () => {
		const url = new URL(window.location.href);
		url.searchParams.delete('activation');
		window.history.replaceState({}, '', url.toString());
	};

	const handleCloseSuccessModal = () => {
		onCloseSuccess();
		removeActivationParameter();
	};

	const handleCloseFailedModal = () => {
		onClosFailed();
		removeActivationParameter();
	};

	const activeMembership: SubscriptionMembership | undefined = useMemo(() => {
		if (!activeMembershipData) return;
		return activeMembershipData.workplaceActiveSubscriptionMembership;
	}, [activeMembershipData]);

	if (
		isLoading ||
		isFetching ||
		isLoadingActiveMembershipData ||
		isFetchingActiveMembershipData
	) {
		return <Loading />;
	}

	let component = <ORSMOnboarding rootWorkplaceId={rootWorkplaceId} />;

	if (data?.workplace?.slug) {
		component = (
			<ORSMDashboard
				slug={data?.workplace?.slug}
				rootWorkplaceId={rootWorkplaceId}
				onDomainChange={refetch}
				onMembershipChange={refetchActiveMembershipData}
				activeMembership={activeMembership}
			/>
		);
	}

	return (
		<>
			{component}
			{isOpenSuccess && (
				<ORSMActivationConfirmationModal
					isOpen
					onClose={handleCloseSuccessModal}
				/>
			)}
			{isOpenFailed && (
				<ORSMActivationFailedModal isOpen onClose={handleCloseFailedModal} />
			)}
		</>
	);
};

export default ORSMWrapper;
